import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import { TABLE4_FIELDS, errorText } from './format';

/**
 * Shared DI1 settings: the contact person NBS replies to (header B17–B23) and
 * which accounts make each Table 4 field. Everyone sees them; the team's
 * managers change them.
 */
export default function SettingsModal({ open, onClose, onSaved }) {
  const { t } = useTranslation('diReports');
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  // Both rule sets are edited; `which` picks the one on screen.
  const [sets, setSets] = useState({ table4Rules: {}, table4RulesFinal: {} });
  const [which, setWhich] = useState('table4Rules');
  const rules = sets[which] || {};
  const setRules = (update) =>
    setSets((all) => ({ ...all, [which]: update(all[which] || {}) }));

  const { data } = useQuery({
    queryKey: ['di-reports-settings'],
    queryFn: () => API.diReports.getSettings(),
    enabled: open,
  });
  const canManage = Boolean(data?.canManage);

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue({ ...data.contact, fillPlace: data.fillPlace });
    setSets({
      table4Rules: JSON.parse(JSON.stringify(data.table4Rules || {})),
      table4RulesFinal: JSON.parse(JSON.stringify(data.table4RulesFinal || {})),
    });
  }, [data, form]);

  const save = useMutation({
    mutationFn: (v) =>
      API.diReports.saveSettings({
        contact: {
          firstName: v.firstName,
          lastName: v.lastName,
          role: v.role,
          phone: v.phone,
          email: v.email,
        },
        fillPlace: v.fillPlace,
        table4Rules: sets.table4Rules,
        table4RulesFinal: sets.table4RulesFinal,
      }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['di-reports-settings'], fresh);
      message.success(t('messages.saved'));
      onSaved?.();
      onClose();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const setRule = (field, idx, patch) =>
    setRules((r) => ({
      ...r,
      [field]: r[field].map((x, i) => (i === idx ? { ...x, ...patch } : x)),
    }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('settings.title')}
      width={820}
      okText={t('actions.save')}
      onOk={() => form.submit()}
      okButtonProps={{
        shape: 'round',
        disabled: !canManage,
        loading: save.isPending,
      }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      {data && !canManage && (
        <Alert
          type="info"
          showIcon
          className="!static mb-3"
          message={t('settings.readOnly')}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={!canManage}
        onFinish={(v) => save.mutate(v)}
      >
        <Divider orientation="left" plain>
          {t('settings.contact')}
        </Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="firstName"
              label={t('settings.firstName')}
              rules={[{ required: true }]}
            >
              <Input maxLength={60} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="lastName"
              label={t('settings.lastName')}
              rules={[{ required: true }]}
            >
              <Input maxLength={60} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="role" label={t('settings.role')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="phone"
              label={t('settings.phone')}
              rules={[{ required: true }]}
              extra={t('settings.phoneHelp')}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="email"
              label={t('settings.email')}
              rules={[{ required: true, type: 'email' }]}
              extra={t('settings.emailHelp')}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="fillPlace"
              label={t('settings.fillPlace')}
              rules={[{ required: true }]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider orientation="left" plain>
        {t('settings.rules')}
      </Divider>
      <Segmented
        className="mb-2"
        value={which}
        onChange={setWhich}
        options={[
          { value: 'table4Rules', label: t('settings.rulesQuarterly') },
          { value: 'table4RulesFinal', label: t('settings.rulesFinal') },
        ]}
      />
      <div className="mb-2 text-xs opacity-70">
        {t('settings.rulesHelp')}
        {which === 'table4RulesFinal' && <> {t('settings.rulesFinalHelp')}</>}
      </div>
      <Table
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={TABLE4_FIELDS.map((key) => ({ key }))}
        columns={[
          {
            title: t('capital.field'),
            dataIndex: 'key',
            width: 300,
            render: (key) => t(`table4.${key}`),
          },
          {
            title: t('settings.accounts'),
            key: 'rules',
            render: (_, { key }) => (
              <Space wrap size={4}>
                {(rules[key] || []).map((r, idx) => (
                  <Space key={idx} size={2}>
                    <Space.Compact size="small">
                      <Select
                        size="small"
                        disabled={!canManage}
                        value={r.sign}
                        options={[
                          { value: 1, label: '+' },
                          { value: -1, label: '−' },
                        ]}
                        onChange={(v) => setRule(key, idx, { sign: v })}
                        style={{ width: 52 }}
                      />
                      <Input
                        size="small"
                        disabled={!canManage}
                        value={r.prefix}
                        onChange={(e) =>
                          setRule(key, idx, {
                            prefix: e.target.value.replace(/\D/g, ''),
                          })
                        }
                        style={{ width: 70 }}
                      />
                    </Space.Compact>
                    {canManage && (
                      <Button
                        size="small"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          setRules((x) => ({
                            ...x,
                            [key]: x[key].filter((_, i) => i !== idx),
                          }))
                        }
                      />
                    )}
                  </Space>
                ))}
                {canManage && (
                  <Button
                    size="small"
                    shape="circle"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setRules((x) => ({
                        ...x,
                        [key]: [...(x[key] || []), { prefix: '', sign: 1 }],
                      }))
                    }
                  />
                )}
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
}
